import type { SignupInput } from '@lib/input/signup.input'
import type { VendorSignupInput } from '@lib/input/vendor-signup.input'
import * as React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { authService } from '@/services/AuthService'
import { tokenStorage } from '@/helpers/tokenStorage'
import { sessionEvents } from '@/helpers/sessionEvents'
import { isApiError, isTransientApiError } from '@/helpers/apiClient'
import logger, { toErrorMessage } from '@/helpers/logger'
import type { LoginInput, User } from '@/schemas/auth.schema'
import type { AcceptInvitationInput, UpdateProfileInput } from '@lib/input/user.input'

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

interface AuthContextValue {
  /** The signed-in user, or `null` when signed out. */
  user: User | null
  /** The current access token, or `null` when signed out. */
  token: string | null
  /** True while the initial "restore session from token" check is in flight. */
  isLoading: boolean
  /** Whether a user is currently authenticated. */
  isAuthenticated: boolean
  /** Exchange credentials for a session; resolves once the user is loaded. */
  login: (input: LoginInput) => Promise<void>
  /** Accept an invitation (set first password) and sign in with the new session. */
  /** Accept an invitation. Resolves with where it meant to land them, or null for wherever their role starts. */
  acceptInvitation: (input: AcceptInvitationInput) => Promise<string | null>
  /** Sign a company up and sign in as its first admin. */
  signup: (input: SignupInput) => Promise<void>
  /** Sign a vendor company up and sign in as its owner. */
  vendorSignup: (input: VendorSignupInput) => Promise<void>
  /**
   * Spend an emailed sign-in link. Resolves with the path the link was issued
   * for, so the caller can finish the journey the person was on when they were
   * bounced to the login page — or null, and they land wherever their role does.
   */
  signInWithMagicLink: (token: string) => Promise<string | null>
  /** Update the signed-in user's own profile (name/avatar), refreshing state. */
  updateProfile: (input: UpdateProfileInput) => Promise<void>
  /**
   * Super-admin support mode: enter a tenant with org-admin powers. Replaces
   * the session tokens and clears the query cache so tenant data does not leak.
   */
  enterTenant: (tenantId: string) => Promise<void>
  /** Leave tenant support mode and return to platform-only access. */
  exitTenant: () => Promise<void>
  /**
   * Enter a venue: venue-admin scope for that venue alone. Replaces the session
   * tokens and clears the query cache, since cached org-wide lists no longer
   * match what the narrowed session may read.
   */
  enterVenue: (venueId: string) => Promise<void>
  /** Leave the entered venue, restoring the previous scope. */
  exitVenue: () => Promise<void>
  /** Clear the session and stored token. */
  logout: () => void
}

const AuthContext = React.createContext<AuthContextValue | null>(null)

interface AuthProviderProps {
  children: React.ReactNode
}

/**
 * Provides authentication state to the app. On mount it restores a session from
 * a persisted token (by fetching the current user); a failed restore clears the
 * token and drops to the signed-out state.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  // helpers → state → effects → handlers → render
  const queryClient = useQueryClient()
  const [user, setUser] = React.useState<User | null>(null)
  const [token, setToken] = React.useState<string | null>(() => tokenStorage.get())
  const [isLoading, setIsLoading] = React.useState<boolean>(true)
  // Read by the cross-tab listener below, which must not be torn down and
  // re-attached every time either value changes. Mirrored in an effect rather
  // than during render, which is not allowed to touch refs.
  const tokenRef = React.useRef<string | null>(null)
  const userRef = React.useRef<User | null>(null)
  React.useEffect(() => {
    tokenRef.current = token
    userRef.current = user
  }, [token, user])

  React.useEffect(() => {
    let active = true

    /**
     * Restore the signed-in user from stored tokens. Only clear the session on a
     * definitive auth rejection — not on network/5xx (deploy blips), so a
     * mid-deploy reload does not force everyone to log in again.
     */
    async function restoreSession(): Promise<void> {
      if (!tokenStorage.get()) {
        setIsLoading(false)
        return
      }
      const maxAttempts = 4
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          const currentUser = await authService.me()
          if (active) setUser(currentUser)
          return
        } catch (error) {
          const transient = isTransientApiError(error)
          const stillHaveTokens = tokenStorage.get() != null
          if (transient && stillHaveTokens && attempt < maxAttempts - 1) {
            logger.warn('Session restore delayed (API unavailable); retrying', {
              attempt: attempt + 1,
              error: toErrorMessage(error),
            })
            await sleep(400 * 2 ** attempt)
            continue
          }
          if (transient && stillHaveTokens) {
            // Give up for now but keep tokens — next navigation/request can recover.
            logger.warn('Session restore deferred; keeping tokens', {
              error: toErrorMessage(error),
            })
            return
          }
          // Definitive failure (401 after invalid refresh, or tokens already cleared).
          logger.warn('Session restore failed; signing out', {
            error: toErrorMessage(error),
            status: isApiError(error) ? error.status : null,
          })
          tokenStorage.clear()
          if (active) {
            setUser(null)
            setToken(null)
          }
          return
        }
      }
    }

    void restoreSession().finally(() => {
      if (active) setIsLoading(false)
    })
    return () => {
      active = false
    }
  }, [])

  // Keep the identity honest when another tab changes the session.
  //
  // The tokens live in localStorage, shared by every tab on this origin, but the
  // signed-in user lives in this tab's React state. Nothing connected the two, so
  // a tab whose session had been replaced elsewhere carried on rendering the old
  // user and sent the *new* user's token: every request came back 403 — properly
  // authenticated, just not as whoever the screen still showed — with no 401 to
  // trigger recovery. Signing out elsewhere did recover, but only by way of a
  // failed request.
  //
  // A token that merely rotated (same person, new value) needs nothing: requests
  // read it from storage each time. It is a change of *identity* that has to be
  // picked up, and cached data from the previous one dropped.
  React.useEffect(() => {
    return tokenStorage.subscribe((nextToken) => {
      if (nextToken === null) {
        setToken(null)
        setUser(null)
        queryClient.clear()
        return
      }
      if (nextToken === tokenRef.current) {
        return
      }
      setToken(nextToken)
      void (async () => {
        try {
          const nextUser = await authService.me()
          if (userRef.current && userRef.current.id !== nextUser.id) {
            queryClient.clear()
          }
          setUser(nextUser)
        } catch (error) {
          if (isTransientApiError(error)) {
            // A blip while resolving is not evidence the identity changed, and
            // dropping the user here would sign someone out mid-deploy. The next
            // request recovers or fails definitively on its own.
            logger.warn('Could not resolve the session another tab changed; keeping the user', {
              error: toErrorMessage(error),
            })
            return
          }
          // Definitive: whoever holds the session now, it is not who this tab shows.
          logger.warn('Session changed in another tab and is no longer usable', {
            error: toErrorMessage(error),
          })
          setUser(null)
          queryClient.clear()
        }
      })()
    })
  }, [queryClient])

  // The fetch layer signs out (clearing tokens) when a 401 can't be recovered
  // by refreshing; mirror that into React state so the UI drops to login at
  // once instead of on the next navigation.
  React.useEffect(() => {
    return sessionEvents.onSignedOut(() => {
      setToken(null)
      setUser(null)
    })
  }, [])

  const applySession = React.useCallback(
    (accessToken: string, refreshToken: string, nextUser: User): void => {
      tokenStorage.setSession(accessToken, refreshToken)
      setToken(accessToken)
      setUser(nextUser)
      // Drop cached tenant-scoped data so enter/exit never show the wrong org.
      queryClient.clear()
    },
    [queryClient],
  )

  const login = React.useCallback(async (input: LoginInput): Promise<void> => {
    const result = await authService.login(input)
    tokenStorage.setSession(result.accessToken, result.refreshToken)
    setToken(result.accessToken)
    setUser(result.user)
  }, [])

  const acceptInvitation = React.useCallback(async (input: AcceptInvitationInput): Promise<string | null> => {
    const result = await authService.acceptInvitation(input)
    tokenStorage.setSession(result.accessToken, result.refreshToken)
    setToken(result.accessToken)
    setUser(result.user)
    return result.redirectTo
  }, [])

  const signup = React.useCallback(async (input: SignupInput): Promise<void> => {
    const result = await authService.signup(input)
    tokenStorage.setSession(result.accessToken, result.refreshToken)
    setToken(result.accessToken)
    setUser(result.user)
  }, [])

  const vendorSignup = React.useCallback(async (input: VendorSignupInput): Promise<void> => {
    const result = await authService.vendorSignup(input)
    tokenStorage.setSession(result.accessToken, result.refreshToken)
    setToken(result.accessToken)
    setUser(result.user)
  }, [])

  const signInWithMagicLink = React.useCallback(async (token: string): Promise<string | null> => {
    const result = await authService.signInWithMagicLink({ token })
    tokenStorage.setSession(result.accessToken, result.refreshToken)
    setToken(result.accessToken)
    setUser(result.user)
    return result.redirectTo
  }, [])

  const updateProfile = React.useCallback(async (input: UpdateProfileInput): Promise<void> => {
    const updated = await authService.updateProfile(input)
    setUser(updated)
  }, [])

  const enterTenant = React.useCallback(
    async (tenantId: string): Promise<void> => {
      const refreshToken = tokenStorage.getRefresh()
      if (!refreshToken) {
        throw new Error('No session to enter a tenant with.')
      }
      const result = await authService.enterTenant(tenantId, refreshToken)
      applySession(result.accessToken, result.refreshToken, result.user)
    },
    [applySession],
  )

  const exitTenant = React.useCallback(async (): Promise<void> => {
    const refreshToken = tokenStorage.getRefresh()
    if (!refreshToken) {
      throw new Error('No session to exit tenant support mode with.')
    }
    const result = await authService.exitTenant(refreshToken)
    applySession(result.accessToken, result.refreshToken, result.user)
  }, [applySession])

  const enterVenue = React.useCallback(
    async (venueId: string): Promise<void> => {
      const refreshToken = tokenStorage.getRefresh()
      if (!refreshToken) {
        throw new Error('No session to enter a venue with.')
      }
      const result = await authService.enterVenue(venueId, refreshToken)
      applySession(result.accessToken, result.refreshToken, result.user)
    },
    [applySession],
  )

  const exitVenue = React.useCallback(async (): Promise<void> => {
    const refreshToken = tokenStorage.getRefresh()
    if (!refreshToken) {
      throw new Error('No session to exit the venue with.')
    }
    const result = await authService.exitVenue(refreshToken)
    applySession(result.accessToken, result.refreshToken, result.user)
  }, [applySession])

  const logout = React.useCallback((): void => {
    // Revoke the refresh token server-side before dropping the local session.
    const refreshToken = tokenStorage.getRefresh()
    if (refreshToken) void authService.logout(refreshToken)
    tokenStorage.clear()
    setToken(null)
    setUser(null)
    queryClient.clear()
  }, [queryClient])

  const value: AuthContextValue = {
    user,
    token,
    isLoading,
    isAuthenticated: user !== null,
    login,
    signInWithMagicLink,
    acceptInvitation,
    signup,
    vendorSignup,
    updateProfile,
    enterTenant,
    exitTenant,
    enterVenue,
    exitVenue,
    logout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * Access the auth context. Throws if used outside an {@link AuthProvider} so a
 * misplaced consumer fails loudly instead of reading a stale default.
 */
export function useAuth(): AuthContextValue {
  const context = React.useContext(AuthContext)
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
